import { z } from 'zod';
import { COLORS } from './schema.ts';

/**
 * Schema content (hero / monster / dungeon / buff pool) — phụ lục 14 của config-table, combat.md 2, 4, 5, 7.
 * Dữ liệu do s7 viết ở packages/content/data/*.json và phải qua parseContent().
 */

const color = z.enum(COLORS);
const elementSchema = z.enum([...COLORS, 'none']);
const id = z.string().regex(/^[a-z][a-zA-Z0-9_-]*$/, 'id: chữ thường đầu, [a-zA-Z0-9_-]');
const nonNegInt = z.int().min(0);
const posInt = z.int().min(1);

export const BOOSTER_TYPES = ['rocketH', 'rocketV', 'bomb', 'propeller', 'lightball'] as const;
export const BUFF_TYPES = [
  'statPct',
  'colorSpawnWeight',
  'boosterShieldTurns',
  'ultGainPct',
  'essenceStartCharge',
] as const;
export const HERO_STATS = [
  'hp',
  'handAttack',
  'abilityAttack',
  'critRateBp',
  'critDmgPct',
  'defense',
] as const;

const fx = <T extends string, S extends z.ZodRawShape>(type: T, shape: S) =>
  z.strictObject({ type: z.literal(type), ...shape });

/** Từ vựng hiệu ứng chung (combat.md 4.3 + 5.2). */
export const effectSchema = z.discriminatedUnion('type', [
  fx('damage', { powerPct: nonNegInt, target: z.enum(['single', 'all', 'random']).optional() }),
  fx('hitRow', { at: z.literal('anchor') }),
  fx('hitColumn', { at: z.literal('anchor') }),
  fx('hitArea', { radius: z.int().min(0).max(4) }),
  fx('hitRandom', { count: posInt }),
  fx('convertColor', { count: posInt, to: color, from: color.optional() }),
  fx('spawnBooster', { booster: z.enum(BOOSTER_TYPES), count: posInt }),
  fx('cleanse', { count: posInt }),
  fx('detonateEssence', { count: z.union([posInt, z.literal('all')]) }),
  fx('shuffle', {}),
  fx('heal', { pctMaxHp: z.int().min(1).max(100) }),
  fx('shield', { pctMaxHp: z.int().min(1).max(100), turns: posInt }),
  fx('stun', { turns: posInt }),
  fx('dot', { pctAA: posInt, turns: posInt }),
  fx('statMod', {
    stat: z.enum([...HERO_STATS, 'atk', 'def', 'res']),
    pct: z.int(),
    turns: posInt,
  }),
  fx('attack', { powerPct: posInt, hits: posInt }),
  fx('lockOrbs', { count: posInt, durationTurns: nonNegInt.optional() }),
  fx('freezeOrbs', { count: posInt, layers: posInt.optional() }),
  fx('deadMark', { count: posInt, turns: posInt.optional(), powerPct: posInt }),
  fx('destroyOrbs', { count: posInt }),
]);
export type Effect = z.infer<typeof effectSchema>;

const skill = (minLen: number, maxLen: number) =>
  z.strictObject({
    id,
    name: z.string().min(1),
    sequence: z.array(color).min(minLen).max(maxLen),
    element: elementSchema,
    powerPct: nonNegInt,
    target: z.enum(['single', 'all', 'random']),
    cooldownTurns: nonNegInt,
    effects: z.array(effectSchema),
  });

export const heroSchema = z.strictObject({
  id,
  name: z.string().min(1),
  artKey: z.string().min(1).optional(),
  stats: z.strictObject({
    hp: posInt,
    handAttack: nonNegInt,
    abilityAttack: nonNegInt,
    critRateBp: z.int().min(0).max(10000),
    critDmgPct: z.int().min(100).max(1000).optional(),
    defense: nonNegInt,
  }),
  skills: z.array(skill(3, 6)).length(3),
  ultimate: skill(5, 7),
});

export const intentSchema = z.strictObject({
  id,
  countdown: posInt,
  weight: posInt.optional(),
  effects: z.array(effectSchema).min(1),
});

export const monsterSchema = z.strictObject({
  id,
  name: z.string().min(1),
  artKey: z.string().min(1).optional(),
  hp: posInt,
  atk: nonNegInt,
  def: nonNegInt,
  res: nonNegInt,
  element: elementSchema,
  pattern: z.enum(['cycle', 'weighted']),
  intents: z.array(intentSchema).min(1),
});

export const dungeonSchema = z.strictObject({
  id,
  name: z.string().min(1),
  floors: z
    .array(
      z.strictObject({
        waves: z.array(z.strictObject({ monsters: z.array(id).min(1).max(5) })).min(1),
      }),
    )
    .min(1),
});

export const buffSchema = z.strictObject({
  id,
  type: z.enum(BUFF_TYPES),
  value: z.int(),
  rarity: z.enum(['common', 'rare', 'epic', 'legendary']),
  stat: z.enum(['hp', 'handAttack', 'abilityAttack']).optional(),
  color: color.optional(),
});

const dupes = (ids: readonly string[]) => ids.filter((v, i) => ids.indexOf(v) !== i);

export const contentSchema = z
  .strictObject({
    heroes: z.array(heroSchema).min(1),
    monsters: z.array(monsterSchema).min(1),
    dungeons: z.array(dungeonSchema).min(1),
    buffPool: z.array(buffSchema),
  })
  .superRefine((c, ctx) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });
    for (const [key, list] of [
      ['heroes', c.heroes],
      ['monsters', c.monsters],
      ['dungeons', c.dungeons],
      ['buffPool', c.buffPool],
    ] as const) {
      const d = dupes(list.map((x) => x.id));
      if (d.length) issue([key], `id trùng: ${d.join(', ')}`);
    }
    const monsterIds = new Set(c.monsters.map((m) => m.id));
    c.dungeons.forEach((dg, di) =>
      dg.floors.forEach((fl, fi) =>
        fl.waves.forEach((w, wi) =>
          w.monsters.forEach((mid, mi) => {
            if (!monsterIds.has(mid))
              issue(
                ['dungeons', di, 'floors', fi, 'waves', wi, 'monsters', mi],
                `monster không tồn tại: ${mid}`,
              );
          }),
        ),
      ),
    );
    c.monsters.forEach((m, mi) => {
      if (m.pattern === 'weighted')
        m.intents.forEach((it, ii) => {
          if (it.weight === undefined)
            issue(['monsters', mi, 'intents', ii, 'weight'], 'pattern weighted cần weight');
        });
    });
    c.buffPool.forEach((b, bi) => {
      if (b.type === 'statPct' && !b.stat) issue(['buffPool', bi, 'stat'], 'statPct cần stat');
      if (b.type === 'colorSpawnWeight' && !b.color)
        issue(['buffPool', bi, 'color'], 'colorSpawnWeight cần color');
    });
  });

export type Content = z.infer<typeof contentSchema>;

export function parseContent(input: unknown): Content {
  return contentSchema.parse(input);
}
